/**
 * What a reviewer is actually being asked to decide, for ONE document
 * approval.
 *
 * This is the body of the repository's own approval queue, lifted out so the
 * generic "My Approvals" inbox can show the same thing. That matters more than
 * tidiness: the inbox lists document approvals alongside transaction ones (they
 * are the same engine, the same levels, the same table), and until it could
 * render this it rendered them as a transaction — a staged record with a
 * `[object Object]` preview and an Edit button that could only ever fail,
 * because `DMS_<repo>` is not a transaction object.
 *
 * Two decisions are baked in.
 *
 * 1. **The diff is against the current release**, not the previous row. What a
 *    reviewer needs to know is what changes if they approve, and for a document
 *    whose last two revisions were both rejected that is not "the version
 *    before this one".
 * 2. **The file itself is optional and OFF by default** (`embedViewer`). The
 *    repository queue sits next to the document list, so it points at the real
 *    viewer rather than duplicating it. The inbox has no such route — it is a
 *    modal over an unrelated screen — so it embeds the watermarked viewer and
 *    the approver reads the real file under the real ACL without leaving.
 */

import { useCallback, useEffect, useState } from 'react'
import { SecureDocViewer } from './SecureDocViewer'
import {
  formatWhen, humaniseState,
  type DmsApprovalDetail, type DmsClient, type DmsDocumentDetail,
  type DmsVersion,
} from './contracts'
import { RADIUS, T, TYPE } from './styles'

export interface DmsApprovalReviewProps {
  client: DmsClient
  /** The approval being reviewed. */
  instanceId: string
  /**
   * A detail the host already loaded. When omitted this component loads it,
   * so either caller shape works without the other having to change.
   */
  detail?: DmsApprovalDetail | null
  /** Read the real file inline (see the note above). */
  embedViewer?: boolean
  /** Open the document in its repository, when the host can navigate there. */
  onOpen?: (documentId: string) => void
  /** Surfaced to the host so it can show the failure in its own chrome. */
  onError?: (message: string) => void
}

interface DiffState {
  loading: boolean
  summary: string
  lines: string[]
  error: string
  unavailable: boolean
}

const EMPTY_DIFF: DiffState = {
  loading: false, summary: '', lines: [], error: '', unavailable: false,
}

export function DmsApprovalReview({
  client, instanceId, detail: given, embedViewer, onOpen, onError,
}: DmsApprovalReviewProps) {
  const [loaded, setLoaded] = useState<DmsApprovalDetail | null>(given ?? null)
  const [diff, setDiff] = useState<DiffState>(EMPTY_DIFF)
  // Only fetched for the embedded viewer: versions + rights are what the
  // viewer needs, and a host that only wants the diff must not pay for them.
  const [doc, setDoc] = useState<DmsDocumentDetail | null>(null)
  const [error, setError] = useState('')

  const fail = useCallback((message: string) => {
    setError(message)
    onError?.(message)
  }, [onError])

  useEffect(() => { setLoaded(given ?? null) }, [given])

  // Load the approval when the host did not hand one over.
  useEffect(() => {
    if (given || !instanceId || !client.approvalDetail) return undefined
    let live = true
    void (async () => {
      try {
        const next = await client.approvalDetail!(instanceId)
        if (live) { setLoaded(next); setError('') }
      } catch (loadError) {
        if (live) {
          fail(loadError instanceof Error ? loadError.message
            : 'This approval could not be opened.')
        }
      }
    })()
    return () => { live = false }
  }, [client, given, instanceId, fail])

  const documentId = loaded?.document_id || ''
  const candidate = loaded?.candidate_version_id || ''
  const kind = loaded?.kind || 'version'

  // The document behind the approval: needed for the diff's "what is in force
  // today" and, when asked for, for the viewer's version + rights.
  useEffect(() => {
    let live = true
    setDoc(null)
    setDiff(EMPTY_DIFF)
    if (!documentId || kind !== 'version') return undefined

    void (async () => {
      let document: DmsDocumentDetail
      try {
        document = await client.detail(documentId)
      } catch (detailError) {
        if (live) {
          setDiff({
            ...EMPTY_DIFF, unavailable: true,
            error: detailError instanceof Error ? detailError.message
              : 'The document behind this approval could not be read.',
          })
        }
        return
      }
      if (!live) return
      setDoc(document)

      if (!candidate) return
      const current = String(document.document.current_version_id || '')
      if (!current || current === candidate) {
        // Nothing is in force yet — a first release has nothing to change
        // FROM, and saying so is more useful than an empty diff.
        setDiff({ ...EMPTY_DIFF, unavailable: true,
                  error: 'This is the first release; there is no earlier '
                         + 'version to compare it against.' })
        return
      }
      setDiff({ ...EMPTY_DIFF, loading: true })
      try {
        const result = await client.compare(documentId, current, candidate)
        if (!live) return
        setDiff({
          loading: false,
          summary: String(result.summary || ''),
          lines: (result.diff || []).filter(
            (line) => !line.startsWith('---') && !line.startsWith('+++')),
          error: String((result as { error?: string }).error || ''),
          unavailable: false,
        })
      } catch (compareError) {
        if (!live) return
        setDiff({
          ...EMPTY_DIFF, unavailable: true,
          error: compareError instanceof Error ? compareError.message
            : 'The versions could not be compared.',
        })
      }
    })()
    return () => { live = false }
  }, [client, documentId, candidate, kind])

  if (!loaded) {
    // A host that serves the repository but not its approval endpoints (the
    // downloaded runtime) can never produce a detail — say so instead of
    // spinning on a request that will not be made.
    const stuck = !given && !client.approvalDetail
    return (
      <p style={mutedStyle}>
        {error
          || (stuck
            ? 'This document approval is decided in the repository it belongs '
              + 'to — this app does not serve approval details here.'
            : 'Loading this document approval…')}
      </p>
    )
  }

  const versions = doc?.versions || []
  const candidateVersion: DmsVersion | null =
    versions.find((v) => v.id === candidate)
    || versions.find((v) => v.is_current)
    || versions[versions.length - 1]
    || null
  const currentVersion = versions.find((v) => v.is_current) || null

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {error ? <p style={errStyle}>{error}</p> : null}

      <header style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {onOpen ? (
          <button type="button" style={linkStyle}
                  onClick={() => onOpen(loaded.document_id)}>
            {loaded.title || 'Untitled'}
          </button>
        ) : (
          <strong style={{ fontSize: TYPE.heading }}>{loaded.title || 'Untitled'}</strong>
        )}
        <p style={{ ...mutedStyle, margin: 0 }}>
          {kind === 'review'
            ? 'A set of review comments, awaiting sign-off. Approving marks '
              + 'them accepted — it does not release a new version.'
            : 'A new version, proposed. The version already in force stays in '
              + 'force until every approver has agreed.'}
        </p>
      </header>

      {/* The facts a decision turns on, stated rather than left in an id. */}
      <dl style={factsStyle}>
        {kind === 'version' ? (
          <>
            <Fact label="Proposed version"
                  value={candidateVersion?.version_label
                         || (candidate ? shortId(candidate) : '—')} />
            <Fact label="In force today"
                  value={currentVersion?.version_label
                         || (doc ? 'Nothing yet' : '…')} />
            <Fact label="If everyone approves"
                  value={loaded.target_state
                    ? humaniseState(loaded.target_state)
                    : '—'} />
            {candidateVersion?.page_count ? (
              <Fact label="Pages" value={String(candidateVersion.page_count)} />
            ) : null}
          </>
        ) : (
          <Fact label="Review" value={shortId(loaded.review_id)} />
        )}
      </dl>

      <SlaLine sla={loaded.sla} />

      {kind === 'version' ? (
        <section>
          <h3 style={sectionTitleStyle}>What changes if you approve</h3>
          {diff.loading ? (
            <p style={mutedStyle}>Comparing with the version in force…</p>
          ) : diff.lines.length > 0 ? (
            <>
              {diff.summary ? (
                <p style={{ fontSize: TYPE.meta, lineHeight: 1.6, margin: '0 0 10px' }}>
                  {diff.summary}
                </p>
              ) : null}
              <pre style={diffStyle}>
                {diff.lines.map((line, index) => (
                  <span key={index} style={diffLineStyle(line)}>{line}{'\n'}</span>
                ))}
              </pre>
            </>
          ) : (
            <p style={mutedStyle}>
              {diff.error
                || 'These two versions could not be compared as text — most '
                   + 'often a scan with no readable text in it.'}
            </p>
          )}
        </section>
      ) : null}

      {embedViewer && kind === 'version' ? (
        <section>
          <h3 style={sectionTitleStyle}>The document itself</h3>
          {!doc ? (
            <p style={mutedStyle}>Loading the document…</p>
          ) : !doc.rights.view ? (
            <p style={mutedStyle}>
              You can decide this, but you are not allowed to read the file itself.
            </p>
          ) : (
            <div style={viewerFrameStyle}>
              <SecureDocViewer
                client={client}
                documentId={loaded.document_id}
                version={candidateVersion}
                rights={doc.rights}
                title={loaded.title}
              />
            </div>
          )}
        </section>
      ) : null}
    </div>
  )
}

/** No SLA configured is NOT "on time", and must not be rendered as if it were. */
export function SlaLine({ sla }: { sla: DmsApprovalDetail['sla'] }) {
  if (!sla?.has_sla) {
    return (
      <p style={{ ...mutedStyle, margin: 0 }}>
        No deadline has been set for this step.
      </p>
    )
  }
  return (
    <p style={{
      margin: 0,
      fontSize: TYPE.small,
      fontWeight: sla.overdue ? 600 : 400,
      color: sla.overdue ? T.error : T.textSecondary,
    }}>
      {sla.overdue
        ? `Overdue by ${Math.round(Number(sla.hours_over || 0))} hours — it was due ${formatWhen(sla.due_at)}`
        : `Due ${formatWhen(sla.due_at)}`}
    </p>
  )
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <dt style={{ fontSize: TYPE.small, color: T.textFaint }}>{label}</dt>
      <dd style={{ margin: 0, fontSize: TYPE.meta, fontWeight: 600 }}>{value}</dd>
    </div>
  )
}

/** An id is not information; its first segment is at least quotable. */
function shortId(value: string | null | undefined): string {
  const text = String(value || '')
  return text ? `#${text.slice(0, 8)}` : '—'
}

// ── styles (shared with the repository's own approval queue) ───────────────

const mutedStyle: React.CSSProperties = {
  fontSize: TYPE.meta,
  lineHeight: 1.5,
  color: T.textSecondary,
}

const errStyle: React.CSSProperties = {
  margin: 0,
  fontSize: TYPE.meta,
  lineHeight: 1.5,
  color: T.error,
}

const linkStyle: React.CSSProperties = {
  background: 'none',
  border: 'none',
  padding: 0,
  fontSize: TYPE.heading,
  fontWeight: 600,
  fontFamily: 'inherit',
  color: T.accent,
  cursor: 'pointer',
  textAlign: 'left',
}

const factsStyle: React.CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: '10px 22px',
  margin: 0,
  padding: '12px 14px',
  borderRadius: RADIUS.md,
  border: `1px solid ${T.border}`,
  background: T.surface2,
}

const sectionTitleStyle: React.CSSProperties = {
  fontSize: TYPE.meta,
  fontWeight: 600,
  color: T.text,
  margin: '0 0 8px',
}

const diffStyle: React.CSSProperties = {
  margin: 0,
  padding: 10,
  maxHeight: 320,
  overflow: 'auto',
  fontSize: TYPE.small,
  lineHeight: 1.6,
  fontFamily: T.mono,
  border: `1px solid ${T.border}`,
  borderRadius: RADIUS.md,
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
}

const viewerFrameStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  height: 460,
  minHeight: 300,
  overflow: 'hidden',
  border: `1px solid ${T.border}`,
  borderRadius: RADIUS.md,
}

function diffLineStyle(line: string): React.CSSProperties {
  if (line.startsWith('+')) {
    return { color: T.success, background: T.successWeak, display: 'block' }
  }
  if (line.startsWith('-')) {
    return { color: T.error, background: T.errorWeak, display: 'block' }
  }
  if (line.startsWith('@@')) {
    return { color: T.textFaint, display: 'block' }
  }
  return { display: 'block' }
}
